// SeedMap AI — Business Portal Layout
// Sidebar navigation for seed company pages

import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Avatar, Divider, Chip, IconButton, Drawer,
  useMediaQuery, useTheme, Tooltip
} from '@mui/material';
import {
  DashboardOutlined,
  CloudUploadOutlined,
  AssessmentOutlined,
  NotificationsOutlined,
  MapOutlined,
  LocalOfferOutlined,
  LogoutOutlined,
  MenuOutlined,
  GrassOutlined,
  ChevronLeft,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import AppSwitcher from '../AppSwitcher';

const NAV_LINKS = [
  { label: 'Dashboard',     icon: <DashboardOutlined />,    path: '/business'              },
  { label: 'Upload Seeds',  icon: <CloudUploadOutlined />,  path: '/business/seed/upload'  },
  { label: 'Order Report',  icon: <AssessmentOutlined />,   path: '/business/order'        },
  { label: 'Field App',     icon: <MapOutlined />,          path: '/business/field'        },
  { label: 'Alerts',        icon: <NotificationsOutlined />,path: '/business/alerts'       },
  { label: 'Pricing',       icon: <LocalOfferOutlined />,   path: '/business/pricing'      },
];

const SIDEBAR_WIDTH = 240;

export default function SeedMapLayout() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { companyProfile, currentUser, logout } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const isActive = (path) =>
    path === '/business'
      ? location.pathname === '/business'
      : location.pathname.startsWith(path);

  const handleLogout = async () => {
    await logout();
    navigate('/business/login');
  };

  const pageTitle = NAV_LINKS.find(l => isActive(l.path))?.label || 'SeedMap AI';

  const SidebarContent = (
    <Box sx={{
      width: SIDEBAR_WIDTH,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      bgcolor: '#0F2027',
      color: '#fff'
    }}>
      {/* Brand */}
      <Box sx={{ p: 2.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <GrassOutlined sx={{ color: '#66BB6A', fontSize: 22 }} />
          <Typography fontWeight={800} fontSize={15} sx={{ color: '#fff', letterSpacing: 0.3 }}>
            SeedMap <Box component="span" sx={{ color: '#66BB6A' }}>AI</Box>
          </Typography>
        </Box>
        <Chip
          label="Business Portal"
          size="small"
          sx={{ bgcolor: 'rgba(102,187,106,0.15)', color: '#81C784', fontSize: 10, height: 18 }}
        />
      </Box>

      {/* Company identity */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: '#1B5E20', width: 36, height: 36, fontSize: 14, fontWeight: 700 }}>
          {(companyProfile?.companyName || currentUser?.email || 'S')[0].toUpperCase()}
        </Avatar>
        <Box sx={{ overflow: 'hidden', flex: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }} noWrap>
            {companyProfile?.companyName || 'Seed Company'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }} noWrap>
            {companyProfile?.plan ? `${companyProfile.plan} Plan` : 'Active'}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2 }} />

      {/* Nav links */}
      <List sx={{ flex: 1, px: 1, py: 1 }}>
        {NAV_LINKS.map(link => {
          const active = isActive(link.path);
          return (
            <ListItemButton
              key={link.path}
              onClick={() => { navigate(link.path); setDrawerOpen(false); }}
              sx={{
                borderRadius: 2, mb: 0.5,
                bgcolor:      active ? 'rgba(102,187,106,0.18)' : 'transparent',
                color:        active ? '#81C784' : 'rgba(255,255,255,0.6)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' },
                transition: '0.15s'
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                {link.icon}
              </ListItemIcon>
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{ fontSize: 13.5, fontWeight: active ? 700 : 400 }}
              />
              {active && (
                <Box sx={{
                  width: 4, height: 24, borderRadius: 2,
                  bgcolor: '#66BB6A', ml: 0.5, flexShrink: 0
                }} />
              )}
            </ListItemButton>
          );
        })}
      </List>

      {/* Logout */}
      <Box sx={{ px: 1, pb: 2 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 1 }} />
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: 'rgba(255,255,255,0.4)',
            '&:hover': { color: '#EF9A9A', bgcolor: 'rgba(239,154,154,0.08)' }
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            <LogoutOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: 13.5 }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F7F9FC' }}>
      {/* Desktop sidebar */}
      {!isMobile && (
        <Box sx={{
          width: SIDEBAR_WIDTH, flexShrink: 0,
          height: '100vh', position: 'sticky', top: 0,
          overflowY: 'auto'
        }}>
          {SidebarContent}
        </Box>
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: SIDEBAR_WIDTH, border: 'none' } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
            <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#fff' }}>
              <ChevronLeft />
            </IconButton>
          </Box>
          {SidebarContent}
        </Drawer>
      )}

      {/* Main area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar */}
        <Box sx={{
          height: 56,
          bgcolor: '#fff',
          borderBottom: '1px solid #E8ECF0',
          display: 'flex', alignItems: 'center',
          px: { xs: 2, md: 3 },
          position: 'sticky', top: 0, zIndex: 100,
          gap: 1.5
        }}>
          {isMobile && (
            <IconButton size="small" onClick={() => setDrawerOpen(true)}>
              <MenuOutlined />
            </IconButton>
          )}
          <GrassOutlined sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {pageTitle}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* App switcher pill — always visible */}
            <AppSwitcher currentApp="seedmap" />
            <Typography variant="caption" color="text.disabled" sx={{ display: { xs: 'none', lg: 'block' } }}>
              {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </Typography>
          </Box>
        </Box>

        {/* Page content */}
        <Box sx={{ flex: 1, overflowX: 'hidden' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
